import { SmartLink } from "@selector/shared/components";
import { type FC } from "react";
import { Card, CardRowDetail, CardRowDetailColumn, CardRowDetailLine } from "widgets";

import { type IPileupVariant } from "../../../app-utils/track-shapes/track-pileup-factory.js";
import { type ProfileDerivedVariant, type Variant, ZygosityEnum } from "../../../graphql.js";
import {
  getShowRelatedVariantRoute,
  type IGenericVariant,
  type IMutantGenome,
} from "../../../redux/modules/genome-browser.js";
import { VARIANT_TYPE } from "../../../settings/settings.js";
import { ProjectGroupsRow } from "../../core/cards/index.js";
import { SelectRangeButton } from "../SelectRangeButton.js";

export interface IVariantProfileTooltipData {
  variant: IGenericVariant;
  mutantGenome: IMutantGenome;
}

const TrackVariantProfileTooltip: FC<IVariantProfileTooltipData> = ({ variant, mutantGenome }) => {
  const isVcfVariant = !("mutId" in variant); // this flag triggers special behavior for vcf variants

  let mutantZygosity: ZygosityEnum | null;
  let profileChar: string;
  let noCall = false; // default false because classic variants have no noCall support

  if (isVcfVariant) {
    // for VCF variants
    const profileVariant = variant.profileVariant?.find(
      (profileVariant) => profileVariant?.genomeId === mutantGenome.id,
    );
    mutantZygosity = profileVariant?.heterozygous ? ZygosityEnum.HeteroZygous : ZygosityEnum.HomoZygous;
    noCall = profileVariant?.noCall ?? false;
    profileChar = profileVariant?.present ? "1" : "0";
  } else {
    // for classic variants
    const profile: ProfileDerivedVariant = variant.profile?.find(
      (profileVariant) => profileVariant?.genomeId === mutantGenome.id,
    );
    mutantZygosity = profile?.zygosity;
    profileChar = profile?.symbol;
  }

  const getProfileName = () => {
    return profileChar === "1" && !isVcfVariant ? (
      <SmartLink
        to={getShowRelatedVariantRoute(mutantGenome, variant)}
        forceExternal
        tooltip={`View this variant in a new tab in the context of the mutant genome ${mutantGenome.name}`}
      >
        {mutantGenome.name}
      </SmartLink>
    ) : (
      mutantGenome.name
    );
  };

  const mutantIsHeterozygous = mutantZygosity === ZygosityEnum.HeteroZygous;
  const profileText = `${
    noCall ? "no call" : profileChar === "-" ? "not applicable" : profileChar === "0" ? "absent" : "present"
  }${mutantIsHeterozygous && !noCall ? ` (Heterozygous)` : ""}`;

  return (
    <Card title={mutantGenome.name} subtitle={profileText} noBorder>
      <CardRowDetail>
        <CardRowDetailColumn>
          <CardRowDetailLine title={"Name"}>{getProfileName()}</CardRowDetailLine>
          {profileText && <CardRowDetailLine title={"Profile"}>{profileText}</CardRowDetailLine>}
          {variant.refAllele && (profileChar === "0" || (profileChar === "1" && mutantIsHeterozygous)) ? (
            <CardRowDetailLine title={"Reference allele"}>{variant.refAllele}</CardRowDetailLine>
          ) : null}
          {variant.altAllele && profileChar === "1" ? (
            <CardRowDetailLine title={"Variant allele"}>{variant.altAllele}</CardRowDetailLine>
          ) : null}
        </CardRowDetailColumn>
      </CardRowDetail>
    </Card>
  );
};

export interface IVariantSymbolTooltipData {
  pileupVariant: IPileupVariant;
  groups: any;
}

const TrackVariantSymbolTooltip: FC<IVariantSymbolTooltipData> = ({ pileupVariant, groups }) => {
  const getSymbolName = (variant: Variant) =>
    variant.mutId ? (
      <SmartLink to={`/variant/${variant.mutId}`} forceExternal>
        {variant.name}
      </SmartLink>
    ) : (
      variant.name
    );
  const type = (variant) => (variant.variantType?.toLowerCase() === VARIANT_TYPE.POINT ? "SNP" : variant.variantType);

  const firstVariant = pileupVariant.variants[0];
  const variantCount: number = pileupVariant.variants.length;

  return variantCount === 1 ? (
    <Card
      subtitle={type(firstVariant)}
      title={firstVariant.name}
      actions={[<SelectRangeButton range={firstVariant.region?.range} />]}
      noBorder
    >
      <CardRowDetail>
        <CardRowDetailColumn>
          <CardRowDetailLine title={"Name"}>{getSymbolName(firstVariant as Variant)}</CardRowDetailLine>
          <CardRowDetailLine title={"Type"}>{type(firstVariant)}</CardRowDetailLine>
          {firstVariant.refAllele ? (
            <CardRowDetailLine title={"Reference allele"}>{firstVariant.refAllele}</CardRowDetailLine>
          ) : null}
          {firstVariant.altAllele ? (
            <CardRowDetailLine title={"Variant allele"}>{firstVariant.altAllele}</CardRowDetailLine>
          ) : null}
        </CardRowDetailColumn>
      </CardRowDetail>
      {groups &&
        [...groups.entries()].map(([project, groups], index) => (
          <ProjectGroupsRow project={project} groups={groups} key={`feature-tooltip-project-groups-row-${index}`} />
        ))}
    </Card>
  ) : (
    <Card
      subtitle={"Zoom in to see individual variants."}
      title={`${variantCount} Variants`}
      style={{ border: 0 }}
      actions={[<SelectRangeButton range={firstVariant.region?.range} />]}
    >
      <CardRowDetail title={"Variants"}>
        <CardRowDetailColumn>
          {pileupVariant.variants?.map((variant, index) => (
            <CardRowDetailLine
              title={getSymbolName(variant as Variant)}
              key={`variant-shape-list-title-${variant.name}-${index}`}
            >
              {type(variant)}
            </CardRowDetailLine>
          ))}
        </CardRowDetailColumn>
      </CardRowDetail>
    </Card>
  );
};

export { TrackVariantProfileTooltip, TrackVariantSymbolTooltip };
